import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import type { StudentAssignment, BibleLocation, Student, WordSelection } from '@skjv/common';
import { AssignmentCard } from './assignment-card';

interface AssignmentEditorProps {
  assignments: StudentAssignment[];
  studyGroupId?: string;
  onChange: (assignments: StudentAssignment[]) => void;
}

/**
 * Assignment editor - shows all students from the study group with full details.
 * Allows assigning readings and questions to each student.
 */
export const AssignmentEditor = observer(function AssignmentEditor({
  assignments,
  studyGroupId,
  onChange,
}: AssignmentEditorProps) {
  // Get students from the selected study group
  const studyGroup = studyGroupId
    ? bibleState.studyGroups.find((g) => g.id === studyGroupId)
    : undefined;
  const students = studyGroup?.students || [];

  // Auto-sync assignments when study group changes
  useEffect(() => {
    if (!studyGroupId || students.length === 0) return;

    // Create assignments for all students, preserving existing data
    const existingByStudentId = new Map(assignments.map((a) => [a.student.id, a]));

    const syncedAssignments: StudentAssignment[] = students.map((student) => {
      const existing = existingByStudentId.get(student.id);
      if (existing) {
        // Update student info in case it changed
        return {
          ...existing,
          student: {
            id: student.id,
            name: student.name,
            readingLevel: student.readingLevel,
            writingLevel: student.writingLevel,
          },
        };
      }
      // Create new assignment for this student
      return {
        student: {
          id: student.id,
          name: student.name,
          readingLevel: student.readingLevel,
          writingLevel: student.writingLevel,
        },
        reviewQuestion: null,
        startingLocation: undefined,
        endingLocation: undefined,
      };
    });

    // Only update if there's a change
    if (JSON.stringify(syncedAssignments) !== JSON.stringify(assignments)) {
      onChange(syncedAssignments);
    }
  }, [studyGroupId, students, assignments, onChange]);

  const handleQuestionChange = (studentId: string, question: string) => {
    const newAssignments = assignments.map((a) => {
      if (a.student.id !== studentId) return a;
      return { ...a, reviewQuestion: question || null };
    });
    onChange(newAssignments);
  };

  const handleStartLocationChange = (studentId: string, location: BibleLocation | null) => {
    const newAssignments = assignments.map((a) => {
      if (a.student.id !== studentId) return a;
      // Any passage change invalidates a previous word pick
      return {
        ...a,
        startingLocation: location || undefined,
        wordSelection: undefined,
        readingFragment: undefined,
      };
    });
    onChange(newAssignments);
  };

  const handleEndLocationChange = (studentId: string, location: BibleLocation | null) => {
    const newAssignments = assignments.map((a) => {
      if (a.student.id !== studentId) return a;
      return {
        ...a,
        endingLocation: location || undefined,
        wordSelection: undefined,
        readingFragment: undefined,
      };
    });
    onChange(newAssignments);
  };

  const handleWordPick = (studentId: string, selection: WordSelection, fragment: string) => {
    const newAssignments = assignments.map((a) => {
      if (a.student.id !== studentId) return a;
      return { ...a, wordSelection: selection, readingFragment: fragment };
    });
    onChange(newAssignments);
  };

  // Get assignment for a student
  const getAssignment = (student: Student): StudentAssignment | undefined => {
    return assignments.find((a) => a.student.id === student.id);
  };

  if (!studyGroupId) {
    return (
      <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
        <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
          Student Assignments
        </h3>
        <p className="py-4 text-center text-sm text-stone-400 dark:text-stone-500">
          Select a study group first to see students.
        </p>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
        <h3 className="mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
          Student Assignments
        </h3>
        <p className="py-4 text-center text-sm text-stone-400 dark:text-stone-500">
          No students in this group. Add students in the Students tab.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-stone-200/50 bg-stone-50/50 p-3 dark:border-stone-700/30 dark:bg-stone-800/30">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-stone-700 dark:text-stone-300">
          Student Assignments ({students.length})
        </h3>
      </div>

      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Drag verse labels to assign readings. Add review questions for each student.
      </p>

      {/* All students from the group */}
      <div className="space-y-3">
        {students.map((student) => (
          <AssignmentCard
            key={student.id}
            student={student}
            assignment={getAssignment(student)}
            onQuestionChange={(question) => handleQuestionChange(student.id, question)}
            onStartLocationChange={(loc) => handleStartLocationChange(student.id, loc)}
            onEndLocationChange={(loc) => handleEndLocationChange(student.id, loc)}
            onWordPick={(selection, fragment) => handleWordPick(student.id, selection, fragment)}
          />
        ))}
      </div>
    </div>
  );
});
